import React from 'react';
import { motion } from 'framer-motion';
import ProgressiveImage from '../ProgressiveImage';
import { DiscoverSettings } from '../../services/DiscoverService';
import { FramingSettings } from '../../types';

const getFramingStyle = (framing?: FramingSettings, isMobile = false): React.CSSProperties => {
    if (!framing) return {};
    const current = isMobile ? framing.mobile : framing.desktop;
    if (!current) return {};
    return {
        objectPosition: `${current.x}% ${current.y}%`,
        transform: `scale(${current.scale})`
    };
};

interface MaterialsSectionProps {
    settings: DiscoverSettings;
    onSelectMaterial: (material: 'carbon' | 'forged') => void;
}

const MaterialsSection: React.FC<MaterialsSectionProps> = ({ settings, onSelectMaterial }) => {
    return (
        <section className="pt-20 md:pt-32 pb-6 md:pb-8 px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                {/* Classic Carbon */}
                <motion.div
                    whileHover={{ y: -10 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onSelectMaterial('carbon')}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    className="group relative h-[450px] md:h-[600px] bg-zinc-900 overflow-hidden border border-white/5 rounded-3xl cursor-pointer"
                >
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80 z-10" />
                    <div className="absolute inset-0">
                        {/* Desktop Image */}
                        <div className="hidden md:block absolute inset-0">
                            <ProgressiveImage
                                src={settings.classic_image_url}
                                alt={settings.classic_title}
                                containerClassName="w-full h-full grayscale opacity-40 transition-transform duration-700 group-hover:scale-110"
                                style={getFramingStyle(settings.classic_framing_settings, false)}
                            />
                        </div>
                        {/* Mobile Image */}
                        <div className="block md:hidden absolute inset-0">
                            <ProgressiveImage
                                src={settings.classic_image_mobile_url || settings.classic_image_url}
                                alt={settings.classic_title}
                                containerClassName="w-full h-full grayscale opacity-50"
                                style={getFramingStyle(settings.classic_framing_settings, true)}
                            />
                        </div>
                        <div className="absolute inset-0 bg-black/20" />
                    </div>
                    <div className="absolute bottom-10 left-8 md:bottom-12 md:left-12 z-20 space-y-3 md:space-y-4">
                        <span className="bg-white/10 backdrop-blur-md px-4 py-1.5 rounded-full text-[10px] font-black tracking-widest uppercase text-white shadow-lg">
                            {settings.classic_badge}
                        </span>
                        <h3 className="text-3xl md:text-4xl font-black uppercase text-white tracking-tight">
                            {settings.classic_title}
                        </h3>
                        <p className="text-zinc-300 md:text-zinc-400 max-w-xs md:max-w-sm text-xs md:text-sm drop-shadow-md">
                            {settings.classic_description}
                        </p>
                    </div>
                </motion.div>

                {/* Forged Carbon */}
                <motion.div
                    whileHover={{ y: -10 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => onSelectMaterial('forged')}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                    className="group relative h-[450px] md:h-[600px] bg-zinc-900 overflow-hidden border border-white/5 rounded-3xl cursor-pointer"
                >
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80 z-10" />
                    <div className="absolute inset-0">
                        {/* Desktop Image */}
                        <div className="hidden md:block absolute inset-0">
                            <ProgressiveImage
                                src={settings.forged_image_url}
                                alt={settings.forged_title}
                                containerClassName="w-full h-full grayscale contrast-125 opacity-40 transition-transform duration-700 group-hover:scale-110"
                                style={getFramingStyle(settings.forged_framing_settings, false)}
                            />
                        </div>
                        {/* Mobile Image */}
                        <div className="block md:hidden absolute inset-0">
                            <ProgressiveImage
                                src={settings.forged_image_mobile_url || settings.forged_image_url}
                                alt={settings.forged_title}
                                containerClassName="w-full h-full grayscale contrast-125 opacity-50"
                                style={getFramingStyle(settings.forged_framing_settings, true)}
                            />
                        </div>
                        <div className="absolute inset-0 bg-black/20" />
                    </div>
                    <div className="absolute bottom-10 left-8 md:bottom-12 md:left-12 z-20 space-y-3 md:space-y-4">
                        <span className="bg-white/20 text-white border border-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-[10px] font-black tracking-widest uppercase shadow-lg">
                            {settings.forged_badge}
                        </span>
                        <h3 className="text-3xl md:text-4xl font-black uppercase italic text-white tracking-tight">
                            {settings.forged_title}
                        </h3>
                        <p className="text-zinc-300 md:text-zinc-400 max-w-xs md:max-w-sm text-xs md:text-sm drop-shadow-md">
                            {settings.forged_description}
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default MaterialsSection;
